// CHA-608 — pure helpers for the Target TTS workbook. TTS routes to the existing NOT_SUBJECT
// determination; it must never enter either the GCC/CPC certificate path or the generic declare path.
export const TTS_TEMPLATE_HEADERS = ['Style No', 'Product ID', 'TTS Report No', 'Document Type', 'Product Type', 'Reason'];

const text = (value) => (value == null ? '' : String(value).trim());
const key = (value) => text(value).toLowerCase().replace(/[^a-z0-9]/g, '');
const styleKey = (value) => text(value).toUpperCase();

export function normalizeTtsProductId(value) {
  // Keep this contract in sync with normalizeCpscProductId in functions/cpscProductId.js.
  const raw = text(value).toUpperCase();
  const match = raw.match(/^PID[-#]([A-Z0-9]{1,64})$/);
  return match ? `PID-${match[1]}` : '';
}

const REQUIRED_HEADERS = {
  styleno: 'Style No',
  productid: 'Product ID',
  ttsreportno: 'TTS Report No',
  documenttype: 'Document Type',
  producttype: 'Product Type',
  reason: 'Reason',
};

export function ttsTemplateGrid(inputRowCount = 500) {
  return [
    ['Target TTS Exemption Upload'],
    ['One row per style and Target Product ID. Repeat the TTS report number for every style it covers. Document Type must be TTS and Product Type must be ADULT.'],
    [],
    TTS_TEMPLATE_HEADERS,
    ...Array.from({ length: inputRowCount }, () => ['', '', '', 'TTS', '', '']),
  ];
}

export function parseTtsTemplateGrid(grid) {
  const rows = Array.isArray(grid) ? grid : [];
  const headerIndex = rows.findIndex((row) => {
    const found = new Set((row || []).map(key));
    return Object.keys(REQUIRED_HEADERS).every((header) => found.has(header));
  });
  if (headerIndex < 0) {
    return { records: [], errors: ['Template headers were not found. Use the downloaded Target TTS template.'], duplicateRows: [] };
  }

  const headerMap = new Map((rows[headerIndex] || []).map((header, index) => [key(header), index]));
  const valueAt = (row, header) => text((row || [])[headerMap.get(header)]);
  const records = [];
  const errors = [];
  const duplicateRows = [];
  const seenStyles = new Map();

  rows.slice(headerIndex + 1).forEach((row, offset) => {
    const rowNumber = headerIndex + offset + 2;
    const rawStyleNo = (row || [])[headerMap.get('styleno')];
    const rawProductId = (row || [])[headerMap.get('productid')];
    const rawReportNo = (row || [])[headerMap.get('ttsreportno')];
    const styleNo = valueAt(row, 'styleno');
    const productIdInput = valueAt(row, 'productid');
    const productId = normalizeTtsProductId(productIdInput);
    const reportNo = valueAt(row, 'ttsreportno');
    const documentType = valueAt(row, 'documenttype');
    const productType = valueAt(row, 'producttype');
    const reason = valueAt(row, 'reason');
    // Downloaded templates prefill TTS. An otherwise-empty input row is still blank.
    if (!styleNo && !productIdInput && !reportNo && !reason
      && (!documentType || documentType.toUpperCase() === 'TTS')
      && (!productType || productType.toUpperCase() === 'ADULT')) return;

    const rowErrors = [];
    if (!styleNo) rowErrors.push('Style No is required');
    if (typeof rawStyleNo === 'number' || /^\d(?:\.\d+)?e[+-]?\d+$/i.test(styleNo)) rowErrors.push('Style No must be stored as text');
    if (styleNo.includes(',')) rowErrors.push('Style No must contain one style only (no commas)');
    if (!productIdInput) rowErrors.push('Product ID is required');
    if (typeof rawProductId === 'number') rowErrors.push('Product ID must be stored as text');
    else if (productIdInput && !productId) rowErrors.push('Product ID must use the PID- or PID# format');
    if (!reportNo) rowErrors.push('TTS Report No is required');
    if (typeof rawReportNo === 'number' || /^\d(?:\.\d+)?e[+-]?\d+$/i.test(reportNo)) rowErrors.push('TTS Report No must be stored as text');
    if (documentType.toUpperCase() !== 'TTS') rowErrors.push('Document Type must be TTS');
    if (productType.toUpperCase() !== 'ADULT') rowErrors.push('Product Type must be ADULT');
    if (rowErrors.length) {
      errors.push(`Row ${rowNumber}: ${rowErrors.join('; ')}`);
      return;
    }

    const normalizedStyle = styleKey(styleNo);
    const prior = seenStyles.get(normalizedStyle);
    if (prior && prior.reportNo !== reportNo) {
      errors.push(`Row ${rowNumber}: Style No already appears under TTS Report No ${prior.reportNo} on row ${prior.rowNumber}`);
      return;
    }
    if (prior && prior.productId !== productId) {
      errors.push(`Row ${rowNumber}: Product ID conflicts with the same style on row ${prior.rowNumber}`);
      return;
    }
    if (prior && prior.styleNo !== styleNo) {
      errors.push(`Row ${rowNumber}: Style No uses different capitalization than row ${prior.rowNumber}`);
      return;
    }
    if (prior && prior.reason !== reason) {
      errors.push(`Row ${rowNumber}: Reason conflicts with the same style and TTS report on row ${prior.rowNumber}`);
      return;
    }
    if (prior) {
      duplicateRows.push({ rowNumber, duplicateOf: prior.rowNumber, styleNo, reportNo });
      return;
    }
    seenStyles.set(normalizedStyle, { rowNumber, styleNo, productId, reportNo, reason });
    records.push({ rowNumber, styleNo, productId, reportNo, documentType: 'TTS', productType: 'ADULT', reason });
  });

  if (!records.length && !errors.length) errors.push('No Target TTS rows were found.');
  return { records, errors, duplicateRows };
}

// The browser cannot infer canonical style identity from a prefix. The secured server resolver owns that
// contract (for example, bare 294323S may resolve to stored JC294323S, while ABL26B and ACV26B remain distinct).
// Reconcile its read-only preflight response across the whole workbook before the first save begins.
export function validateTtsCanonicalStyles(records, verdicts) {
  const byRequestedStyle = new Map((verdicts || []).map((verdict) => [
    styleKey(verdict?.requestedStyleNo || verdict?.styleNo), verdict,
  ]));
  const seenCanonical = new Map();
  const errors = [];

  for (const record of records || []) {
    const verdict = byRequestedStyle.get(styleKey(record.styleNo));
    if (!verdict) {
      errors.push(`Row ${record.rowNumber}: Server preflight returned no result for Style No ${record.styleNo}`);
      continue;
    }
    if (verdict.eligible !== true) {
      errors.push(`Row ${record.rowNumber}: ${verdict.reason || 'Server preflight did not confirm this style is eligible for a not-subject record'}`);
      continue;
    }
    const canonicalStyleNo = text(verdict.canonicalStyleNo || verdict.styleNo);
    if (!canonicalStyleNo) {
      errors.push(`Row ${record.rowNumber}: Server preflight returned no canonical style for Style No ${record.styleNo}`);
      continue;
    }
    const canonicalKey = styleKey(canonicalStyleNo);
    const prior = seenCanonical.get(canonicalKey);
    if (prior && styleKey(prior.styleNo) !== styleKey(record.styleNo)) {
      errors.push(`Row ${record.rowNumber}: Style No ${record.styleNo} resolves to stored style ${canonicalStyleNo}, already used by ${prior.styleNo} on row ${prior.rowNumber}`);
      continue;
    }
    seenCanonical.set(canonicalKey, record);
  }

  return errors;
}

export function groupTtsRecords(records) {
  const groups = new Map();
  for (const record of records || []) {
    const groupKey = `${record.reportNo}\u0000${record.reason}`;
    if (!groups.has(groupKey)) groups.set(groupKey, { reportNo: record.reportNo, reason: record.reason, rows: [] });
    groups.get(groupKey).rows.push(record);
  }
  return Array.from(groups.values());
}

// The server's compliance classifier intentionally caps one save at 100 styles. Split a large TTS report
// into bounded calls while preserving its report number and reason.
export function batchTtsRecords(records, maxStyles = 100) {
  return groupTtsRecords(records).flatMap((group) => {
    const batches = [];
    for (let index = 0; index < group.rows.length; index += maxStyles) {
      batches.push({ ...group, rows: group.rows.slice(index, index + maxStyles) });
    }
    return batches;
  });
}
